import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import {
  Sparkles,
  School,
  GraduationCap,
  Lightbulb,
  FileQuestion,
  BookOpen,
  LogIn,
  LogOut,
  ChevronDown,
  Menu,
  X,
  ShoppingCart,
  Home,
  User,
  MessageCircle,
  Bell,
} from "lucide-react";

/**
 * Unified Portal Header that matches the landing page SharedHeader design
 * Used across Student, Teacher, Parent, and School portals
 * 
 * Props:
 * - portalType: "student" | "teacher" | "parent" | "school"
 * - userName: Display name of the logged-in user
 * - userSubtitle: Additional info (grade, school, etc.)
 * - onLogout: Callback when logout is clicked
 * - rightContent: Optional additional content for the right side
 * - showNav: Whether to show the main navigation (default: false for portals)
 */
export default function UnifiedPortalHeader({ 
  portalType = "student",
  userName = "",
  userSubtitle = "",
  onLogout,
  rightContent = null,
  showNav = false,
  onMobileMenuClick,
  showMobileMenuButton = false,
  showUserInfo = true,
  largeLogout = false,
  showHomeButton = true,
  hideLogout = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount, setIsCartOpen } = useCart();
  const cartCount = getCartCount();
  const [eduOneOpen, setEduOneOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Portal type labels
  const portalLabels = {
    student: "Student Portal",
    teacher: "Teacher Portal",
    parent: "Parent Portal",
    school: "School Portal",
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setEduOneOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="w-full py-2 px-4 sm:px-6 lg:px-8 bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50" data-testid="portal-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Logo Section */}
        <div className="flex items-center gap-1">
          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden text-gray-600 hover:bg-gray-100 mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          
          <div className="flex flex-col">
            <img 
              src="/eduzy-logo-transparent.png" 
              alt="Eduzy - AI-Powered Learning Platform" 
              className="h-24 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              style={{ height: '5rem' }}
              onClick={() => navigate("/")}
              data-testid="header-logo"
            />
          </div>
          <a 
            href="https://contentiumtech.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:opacity-100 opacity-80 transition-opacity hidden sm:block"
          >
            <img 
              src="/contentium-tech-logo.svg" 
              alt="Contentium Tech" 
              className="h-auto"
              style={{ height: '4rem' }}
              data-testid="header-contentium-logo"
            />
          </a>
        </div>

        {/* Desktop Navigation (when enabled) */}
        {showNav && (
          <div className="hidden lg:flex items-center gap-2">
            {/* EduOne AI Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                onMouseEnter={() => setEduOneOpen(true)}
                onClick={() => setEduOneOpen(!eduOneOpen)}
                variant="ghost"
                className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
                data-testid="nav-eduone-btn"
              >
                <Sparkles className="w-4 h-4" />
                EduOne AI
                <ChevronDown className={`w-4 h-4 transition-transform ${eduOneOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {/* EduOne AI Dropdown Menu */}
              {eduOneOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9999]"
                  onMouseLeave={() => setEduOneOpen(false)}
                >
                  <button
                    onClick={() => { navigate("/eduwise"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                  >
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduWise</div>
                      <div className="text-xs text-gray-500">Personalized AI Tutor</div>
                    </div>
                  </button>
                  <button
                    onClick={() => { navigate("/eduassist"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50 transition-colors"
                  >
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduAssist</div>
                      <div className="text-xs text-gray-500">Personal AI Homework Assistant</div>
                    </div>
                  </button>
                  <button
                    onClick={() => { navigate("/eduquest"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors"
                  >
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileQuestion className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduQuest</div>
                      <div className="text-xs text-gray-500">Book-Grounded Question Banks</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* EduSphere AI Button */}
            <Button
              onClick={() => navigate("/edusphere")}
              variant="ghost"
              className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
            >
              <div className="relative w-4 h-4">
                <div className="absolute inset-0 border-2 border-indigo-400 rounded-full"></div>
                <School className="w-2.5 h-2.5 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              EduSphere AI
            </Button>

            {/* Blog Button */}
            <Button
              onClick={() => navigate("/blog")}
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </Button>

            {/* Cart Button */}
            <Button
              onClick={() => setIsCartOpen(true)}
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10 relative"
            >
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Portal User Section (Right Side) */}
        <div className="flex items-center gap-3">
          {/* Custom Right Content */}
          {rightContent}
          
          {/* Home Button - conditionally rendered (hidden on mobile) */}
          {showHomeButton && (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:bg-gray-100 hidden lg:flex"
              onClick={() => navigate("/")}
              data-testid="home-btn"
            >
              <Home className="h-4 w-4 mr-1.5" />
              Home
            </Button>
          )}

          {/* User Info - conditionally rendered */}
          {showUserInfo && userName && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
              <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800 leading-tight">{userName}</p>
                {userSubtitle && (
                  <p className="text-xs text-gray-500 leading-tight">{userSubtitle}</p>
                )}
              </div>
            </div>
          )}

          {/* Logout Button - always visible with label */}
          {!hideLogout && (
            <Button
              onClick={onLogout}
              variant={largeLogout ? "default" : "outline"}
              size={largeLogout ? "default" : "sm"}
              className={`${largeLogout 
                ? "bg-red-500 hover:bg-red-600 text-white font-semibold px-3 sm:px-6 py-2 shadow-md hover:shadow-lg transition-all"
                : "border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-red-600 hover:border-red-300 px-2 sm:px-3"
              }`}
              data-testid="logout-btn"
            >
              <LogOut className={largeLogout ? "h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" : "h-4 w-4 mr-1 sm:mr-1.5"} />
              <span className="text-xs sm:text-sm">Logout</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50">
          <nav className="px-4 py-3 space-y-1">
            {/* Portal Info */}
            {showUserInfo && userName && (
              <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-lg mb-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{userName}</p>
                  {userSubtitle && <p className="text-sm text-gray-500">{userSubtitle}</p>}
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <button
              onClick={() => { navigate("/"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Home className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700">Home</span>
            </button>

            <button
              onClick={() => { navigate("/eduwise"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-blue-50 rounded-lg transition-colors"
            >
              <GraduationCap className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-700">EduWise</span>
            </button>

            <button
              onClick={() => { navigate("/eduassist"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-purple-50 rounded-lg transition-colors"
            >
              <Lightbulb className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-700">EduAssist</span>
            </button>

            <button
              onClick={() => { navigate("/eduquest"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-amber-50 rounded-lg transition-colors"
            >
              <FileQuestion className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-gray-700">EduQuest</span>
            </button>

            <button
              onClick={() => { navigate("/why-eduzy"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-emerald-50 rounded-lg transition-colors"
            >
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-gray-700">Why Eduzy?</span>
            </button>

            <button
              onClick={() => { navigate("/blog"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
            >
              <BookOpen className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700">Blog</span>
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
